import { reaction, toJS } from "mobx";
import { log } from "@/lib/log";
import { Guide } from "@common/models";
import { RawGuide } from "@common/api/ping";
import { Checklist, ChecklistItem } from "@common/schemas/checklist";
import { PersistentStorage } from "../tour/persistent-storage/storage";
import { EventEmitter } from "../events/eventEmitter";
import { SchedulerEvents, SchedulerNamespace } from "./scheduler.events";
import { ChecklistEvaluationResultWithOrder, GuideEvaluationResultWithOrder } from "@/stores/TargetingEvaluator";
import { SyncResult } from "../SyncManager";
import { TourSessionManager } from "../tour/tourSessionManager";
import { TourState } from "../tour/persistent-storage/tourState";
import { ChecklistRegistry } from "@/stores/ChecklistRegistry";

export interface SchedulerOptions {
  previewGuideId?: string;
  previewChecklistId?: string;
  sharedGuideId?: string;
  previewOnly?: boolean;
}

interface TargetingEvaluator {
  firstMatchingGuideId: string | null;
  firstMatchingChecklistId: string | null;
  evaluatedGuides: GuideEvaluationResultWithOrder[];
  evaluatedChecklists: ChecklistEvaluationResultWithOrder[];
}

interface GuideRegistry {
  findById(guideId: string): RawGuide | undefined;
}

interface PyneClient {
  currentUser: { id: string; traits: Record<string, any> } | null;
}

interface SyncManager {
  syncGuides(params: {
    previewGuideId?: string;
    activeGuideId?: string;
    activeChecklistId?: string;
    previewChecklistId?: string;
    tourPlaybackVersion?: string;
  }): Promise<SyncResult | undefined>;
}

interface SchedulerParams {
  options?: SchedulerOptions;
  targetingEvaluator: TargetingEvaluator;
  guideRegistry: GuideRegistry;
  checklistRegistry: ChecklistRegistry;
  pyneClient: PyneClient;
  syncManager: SyncManager;
  storage: PersistentStorage;
  tourSessionManager: TourSessionManager;
}

export type PreparedGuide = {
  guideId: string;
  stepId?: string;
  preparedFor: "preview" | "shared";
};

export type PreparedChecklist = {
  checklistId: string;
  preparedFor: "preview";
};

export type GuideScheduleReason =
  | "user_targeted"
  | "preview"
  | "shared"
  | "tour_session_recovered"
  | "trigger_from_pyne_client"
  | "trigger_from_other_tour"
  | "trigger_from_debugger"
  | "trigger_from_checklist";

export type ChecklistScheduleReason =
  | "user_targeted"
  | "checklist_session_recovered"
  | "preview"
  | "shared"
  | "trigger_from_pyne_client"
  | "trigger_from_debugger";

export class Scheduler {
  private currentGuide?: Guide;
  private currentChecklist?: Checklist;
  private options?: SchedulerOptions;
  private isInitialized = false;
  private status: "initialized" | "started" | "resyncing" | "synced" | "stopped" = "initialized";
  private targetingEvaluator: TargetingEvaluator;
  private pyneClient: PyneClient;
  private syncManager: SyncManager;
  private storage: PersistentStorage;
  private tourSessionManager: TourSessionManager;
  private guideRegistry: GuideRegistry;
  private checklistRegistry: ChecklistRegistry;
  private preparedGuide?: PreparedGuide;
  private preparedChecklist?: PreparedChecklist;

  events = new EventEmitter<typeof SchedulerNamespace, SchedulerEvents>(SchedulerNamespace);

  static readonly initialSyncGuidesDelay = 50;
  static readonly syncGuidesInterval = 1000;
  static readonly scheduleGuideInterval = 1000;

  constructor({
    targetingEvaluator,
    pyneClient,
    syncManager,
    guideRegistry,
    checklistRegistry,
    storage,
    tourSessionManager,
    options,
  }: SchedulerParams) {
    this.targetingEvaluator = targetingEvaluator;
    this.pyneClient = pyneClient;
    this.syncManager = syncManager;
    this.guideRegistry = guideRegistry;
    this.checklistRegistry = checklistRegistry;
    this.storage = storage;
    this.options = options;
    this.tourSessionManager = tourSessionManager;
  }

  start() {
    if (this.isInitialized && this.status === "stopped") {
      this.status = "synced";
    }

    if (this.isInitialized) {
      return;
    }

    if (this.previewGuideId) {
      this.startGuidePreviewSession();
    }

    if (this.previewChecklistId) {
      this.startPreviewChecklistSession();
    }

    if (this.sharedGuideId) {
      this.startSharedSession();
    }

    log("Guide scheduler starting");

    this.status = "started";
    if (!this.isInitialized) {
      this.initGuideSync();
      this.initPeriodicGuidePicker();
      this.isInitialized = true;
    }
  }

  stop() {
    this.status = "stopped";
    this.clearCurrentGuide();
  }

  switchGuide(guideId: string, reason: "trigger_from_pyne_client" | "trigger_from_other_tour") {
    this.startGuideById(guideId, reason);
  }

  clearCurrentGuide() {
    this.currentGuide = undefined;
  }

  clearCurrentChecklist() {
    this.currentChecklist = undefined;
  }

  private initGuideSync() {
    log("Guide sync initializing");
    // Run when event loop is free
    setTimeout(async () => {
      log("will sync guides");
      await this.syncGuides();
    }, Scheduler.initialSyncGuidesDelay);

    setInterval(async () => {
      if (this.status === "synced" && this.isUserStateRemoved()) {
        this.status = "resyncing";
        await this.syncGuides();
      }
    }, Scheduler.syncGuidesInterval);

    // Run when user is identified
    reaction(
      () => this.pyneClient.currentUser,
      async () => {
        await this.syncGuides();
      }
    );
  }

  private initPeriodicGuidePicker() {
    log("periodic guide picker initialized");

    const schedule = () => {
      log("scheduling...", !this.currentGuide, this.status, this.isAlreadyInSync());
      if (!this.currentGuide && this.isAlreadyInSync()) {
        log("trying to schedule guide");
        this.scheduleGuide();
        this.scheduleChecklist();
      }
    };

    setInterval(() => {
      schedule();
    }, Scheduler.scheduleGuideInterval);
  }

  private isUserStateRemoved() {
    return this.status === "synced" && !this.storage.userState.isExists();
  }

  private isAlreadyInSync() {
    return this.status === "synced" && this.storage.userState.isExists();
  }

  private async syncGuides() {
    log("syncing guides");

    const tourSessionToRecover = this.tourSessionManager.getRecoverableTourSession();
    const syncResult = await this.syncManager.syncGuides({
      previewGuideId: this.previewGuideId,
      activeGuideId: tourSessionToRecover?.activeTourGuideId,
      previewChecklistId: this.previewChecklistId,
      activeChecklistId: this.storage.checklistState.getActiveChecklistId(),
      tourPlaybackVersion: tourSessionToRecover?.tourPlaybackVersion,
    });

    if (!syncResult) {
      return;
    }

    this.storage.userState.hydrate(syncResult.userState);

    this.status = "synced";
    log("syncResult", syncResult, this.status);
  }

  private startGuidePreviewSession() {
    if (!this.previewGuideId) {
      return;
    }

    log("starting preview session", this.previewGuideId);
    this.prepareTourForScheduling({ guideId: this.previewGuideId, preparedFor: "preview" });
  }

  private startPreviewChecklistSession() {
    if (!this.previewChecklistId) {
      return;
    }

    this.prepareChecklistForScheduling({ checklistId: this.previewChecklistId, preparedFor: "preview" });
  }

  private startSharedSession() {
    if (!this.sharedGuideId) {
      return;
    }

    this.prepareTourForScheduling({ guideId: this.sharedGuideId, preparedFor: "shared" });
  }

  private prepareTourForScheduling(preparedGuide: PreparedGuide) {
    this.preparedGuide = preparedGuide;
  }

  private prepareChecklistForScheduling(preparedChecklist: PreparedChecklist) {
    this.preparedChecklist = preparedChecklist;
  }

  private clearPreparedGuide() {
    this.preparedGuide = undefined;
  }

  private clearPreparedChecklist() {
    this.preparedChecklist = undefined;
  }

  private scheduleChecklist() {
    const isGuidePreview = this.isGuidePreviewEnabled();
    if (isGuidePreview) {
      return;
    }

    const isCurrentChecklistExists = !!this.currentChecklist;
    const isAnyChecklistAvailable = this.targetingEvaluator.evaluatedChecklists.length > 0;
    if (!isAnyChecklistAvailable || isCurrentChecklistExists) {
      return;
    }

    if (this.preparedChecklist) {
      this.startChecklistById(this.preparedChecklist.checklistId, this.preparedChecklist.preparedFor);
      this.clearPreparedChecklist();
      return;
    }

    // Preview mode, no further checklists needs to be scheduled
    if (this.isPreviewEnabled()) {
      return;
    }

    const activeChecklistId = this.storage.checklistState.getActiveChecklistId();
    if (activeChecklistId && !this.isChecklistCompleted(activeChecklistId)) {
      this.startChecklistById(activeChecklistId, "checklist_session_recovered");
      return;
    }

    const checklistId = this.targetingEvaluator.firstMatchingChecklistId;
    if (!checklistId) {
      return;
    }

    this.startChecklistById(checklistId, "user_targeted");
  }

  private isChecklistCompleted(checklistId: string) {
    const checklist = this.checklistRegistry.findById(checklistId);
    if (!checklist) {
      return false;
    }

    const finishedGuideIds = this.storage.userState.finishedTourGuideIds;

    const isCompleted = checklist.items.every((item: ChecklistItem) => finishedGuideIds.includes(item.guide_id));
    return isCompleted;
  }

  private scheduleGuide() {
    const isChecklistPreview = this.isChecklistPreviewEnabled();
    if (isChecklistPreview) {
      return;
    }

    const isAnyGuideAvailable = this.targetingEvaluator.evaluatedGuides.length > 0;
    if (!isAnyGuideAvailable) {
      return;
    }

    const tourStateToRecover = this.tourSessionManager.getRecoverableTourSession();

    if (this.preparedGuide) {
      this.startGuideById(this.preparedGuide.guideId, this.preparedGuide.preparedFor, tourStateToRecover);
      this.clearPreparedGuide();
      return true;
    }

    // Start if there is a recoverable tour session
    if (tourStateToRecover) {
      this.startGuideById(tourStateToRecover.activeTourGuideId!, "tour_session_recovered", tourStateToRecover);
      return true;
    }

    // Do not search for other guides if we are in preview mode
    if (this.isPreviewEnabled()) {
      log("preview mode, not scheduling guide");
      return false;
    }

    if (this.options?.previewOnly) {
      log("preview only mode, not scheduling guide");
      return false;
    }

    const guideId = this.targetingEvaluator.firstMatchingGuideId;
    if (guideId) {
      const guide = this.guideRegistry.findById(guideId);
      if (!guide) return false;

      log("starting first matching guide", toJS(guide));
      const guideModel = new Guide(guide);
      this.startGuide(guideModel, "user_targeted");

      return true;
    }

    log("no guide found");
    return false;
  }

  private startGuideById(guideId: string, scheduleReason: GuideScheduleReason, tourState?: TourState) {
    const guide = this.guideRegistry.findById(guideId);

    if (guide) {
      const guideModel = new Guide(guide);
      this.startGuide(guideModel, scheduleReason, tourState);
      return true;
    }

    return false;
  }

  private startGuide(guide: Guide, scheduleReason: GuideScheduleReason, tourState?: TourState) {
    log("starting guide", guide.id, guide.title);
    if (scheduleReason === "user_targeted") {
      this.events.emit("userTargeted", { guide });
    }
    this.currentGuide = guide;
    this.events.emit("guideScheduled", { guide, scheduleReason, tourState });
  }

  private startChecklistById(checklistId: string, scheduleReason: ChecklistScheduleReason) {
    const checklist = this.checklistRegistry.findById(checklistId);
    if (checklist) {
      this.startChecklist(checklist, scheduleReason);
    }
  }

  private startChecklist(checklist: Checklist, scheduleReason: ChecklistScheduleReason) {
    this.currentChecklist = checklist;
    this.events.emit("checklistScheduled", { checklist, scheduleReason });
  }

  private isGuidePreviewEnabled() {
    return !!this.options?.previewGuideId;
  }

  private isChecklistPreviewEnabled() {
    return !!this.options?.previewChecklistId;
  }

  private isPreviewEnabled() {
    return this.isGuidePreviewEnabled() || this.isChecklistPreviewEnabled();
  }

  private get previewGuideId() {
    return this.options?.previewGuideId;
  }

  private get previewChecklistId() {
    return this.options?.previewChecklistId;
  }

  private get sharedGuideId() {
    return this.options?.sharedGuideId;
  }
}
